import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { PlusIcon } from 'lucide-react'
import { WorkflowLevelRow } from './workflow-level-row'
import { emptyStep, type WorkflowMode, type WorkflowStepDraft } from './workflow-levels'

export type { WorkflowStepDraft } from './workflow-levels'

/**
 * The path drawn top to bottom, one row per level (ADR-0034). In a chain
 * every level approves in turn; in an escalation path one level decides
 * and the request moves down this list only when it is escalated.
 */
export function WorkflowStepsEditor({
  companyId,
  steps,
  mode,
  onChange,
  error,
}: {
  companyId: string
  steps: WorkflowStepDraft[]
  mode: WorkflowMode
  onChange: (steps: WorkflowStepDraft[]) => void
  error?: string | null
}) {
  const { t } = useTranslation('workflow')

  function move(index: number, offset: -1 | 1) {
    const target = index + offset
    const current = steps[index]
    const displaced = steps[target]
    if (!current || !displaced) return
    const reordered = [...steps]
    reordered[index] = displaced
    reordered[target] = current
    onChange(reordered)
  }

  return (
    <div className="border-t border-border pt-3">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-h3">{mode === 'escalation' ? t('levels.title') : t('form.steps')}</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChange([...steps, emptyStep(crypto.randomUUID())])}
        >
          <PlusIcon className="size-3.5" aria-hidden="true" />
          {mode === 'escalation' ? t('levels.add') : t('form.addStep')}
        </Button>
      </div>
      <ol className="space-y-0">
        {steps.map((step, index) => (
          <WorkflowLevelRow
            key={step.clientKey}
            companyId={companyId}
            step={step}
            index={index}
            count={steps.length}
            mode={mode}
            onChange={(next) =>
              onChange(steps.map((candidate, i) => (i === index ? next : candidate)))
            }
            onRemove={() => onChange(steps.filter((_, i) => i !== index))}
            onMove={(offset) => move(index, offset)}
          />
        ))}
      </ol>
      {error ? (
        <p className="mt-2 text-small text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
